import { Directive, ElementRef, HostListener, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { EquationService } from '../equation-editor/equation.service';
import { InputService } from '../../sabis-chatbot/input/input.service';

/**
 * Directive that encapsulates the "copy selected text to input" behaviour for
 * bot message bubbles.
 *
 * Apply it to the message content element and export the directive instance
 * via a template variable (exportAs: 'copyToInput') so the host template can
 * read state (showCopyToInputIcon, copyBtnPosition) and call
 * copySelectionToInput() from the copy button.
 *
 * Example usage in template:
 *   <div sabisCopyToInput #copyToInput="copyToInput" #messageContent ...></div>
 *   @if (copyToInput.showCopyToInputIcon) { <button ... (click)="copyToInput.copySelectionToInput()"> }
 */
@Directive({
  selector: '[sabisCopyToInput]',
  exportAs: 'copyToInput',
  standalone: true,
})
export class CopyToInputDirective implements OnInit, OnDestroy {
  public showCopyToInputIcon = false;
  public selectedText = '';
  public copyBtnPosition: { top: number; left: number } = { top: 0, left: 0 };

  private _suppressNextPointerUp = false;

  // Track pointer target to support selection of equations via pointer (since they are non-text nodes that can lead to collapsed selections on pointerup)
  private _lastPointerDownTarget: EventTarget | null = null;


  /**
   * Handles the pointerdown event on the host element. Tracks the target element
   * to support selection of equations via pointer (since they are non-text nodes
   * that can lead to collapsed selections on pointerup).
   * @param event The pointerdown event.
   */
  @HostListener('pointerdown', ['$event'])
  public onHostPointerDown(event: PointerEvent): void {
    this._lastPointerDownTarget = event.target;
  }


  /**
   * Handles global selection changes while the copy UI is active. Ensures the
   * selection is still valid and inside the host element, hides copy state when
   * invalid, and repositions the copy button when valid.
   */
  private onSelectionChange = (): void => {
    const range = this.getActiveRangeWithinHost();
    if (!range) {
      this.clearCopyState();
      this._cdr.markForCheck();
      return;
    }

    const extracted = this.getExtractedSelection(range);
    if (!extracted) {
      this.clearCopyState();
      this._cdr.markForCheck();
      return;
    }

    this.selectedText = extracted;
    this.showCopyToInputIcon = true;
    this.updateCopyButtonPosition(range);
    this._cdr.markForCheck();
  };

  /**
   * Handles scroll events while the copy UI is active. Ensures the
   * selection is still valid and inside the host element, hides copy state when
   * invalid, and repositions the copy button when valid.
   */
  private onScroll = (): void => {
    if (!this.showCopyToInputIcon) return;

    const range = this.getActiveRangeWithinHost();
    if (!range) {
      this.clearCopyState();
      this._cdr.markForCheck();
      return;
    }

    const extracted = this.getExtractedSelection(range);
    if (!extracted) {
      this.clearCopyState();
      this._cdr.markForCheck();
      return;
    }

    this.selectedText = extracted;
    this.updateCopyButtonPosition(range);
    this._cdr.markForCheck();
  };

  constructor(
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _inputService: InputService,
    private readonly _equationService: EquationService,
    /**
     * Used to manually request change detection after native DOM listeners
     * (selection/scroll/pointer) update directive state outside Angular bindings.
     */
    private readonly _cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    document.addEventListener('selectionchange', this.onSelectionChange);
    document.addEventListener('scroll', this.onScroll, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('selectionchange', this.onSelectionChange);
    document.removeEventListener('scroll', this.onScroll, true);
  }

  /**
   * Intercepts the context-menu event on the host element. If the user has
   * selected text that originates within the host, the browser's default
   * context menu is suppressed and the copy-to-input flow is triggered instead.
   */
  @HostListener('contextmenu', ['$event'])
  public onContextMenu(event: MouseEvent | TouchEvent): void {
    const range = this.getActiveRangeWithinHost();
    if (!range) {
      return;
    }

    const extracted = this.getExtractedSelection(range);
    if (!extracted) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.selectedText = extracted;
    this.showCopyToInputIcon = true;
    this.updateCopyButtonPosition(range);
    this._cdr.markForCheck();
  }

  /**
   * Handles the pointerup event on the host element. Checks whether the user
   * has selected text that originates within the host, extracts it (preserving
   * any embedded equations), calculates the position for the copy button
   * relative to the host container, and makes the button visible. Clears the
   * selection state when no valid text is selected.
   */
  @HostListener('document:pointerup')
  public onPointerUp(): void {
    if (this._suppressNextPointerUp) {
      this._suppressNextPointerUp = false;
      return;
    }
    let range = this.getActiveRangeWithinHost();

    // If pointer-up left us with a collapsed selection on equation SVG,
    // synthesize an element selection for the nearest mjx-container.
    if (!range && this.trySelectEquationFromPointerTarget()) {
      range = this.getActiveRangeWithinHost();
    }

    if (!range) {
      this.clearCopyState();
      return;
    }

    const extracted = this.getExtractedSelection(range);
    if (!extracted) {
      this.clearCopyState();
      return;
    }

    this.selectedText = extracted;
    this.showCopyToInputIcon = true;
    this.updateCopyButtonPosition(range);
  }

  /** Updates the position of the copy button based on the current selection range */
  private updateCopyButtonPosition(range: Range): void {
    const rects = range.getClientRects();
    if (!rects.length) return;

    // Determine selection direction by comparing anchor and focus positions
    const selection = window.getSelection();
    let isBackward = false;

    if (selection?.anchorNode && selection?.focusNode) {
      const comparison = selection.focusNode.compareDocumentPosition(selection.anchorNode);
      isBackward = (comparison & Node.DOCUMENT_POSITION_PRECEDING) !== 0;
    }

    const targetRect = isBackward ? rects[rects.length - 1] : rects[0];
    const containerRect = this._elementRef.nativeElement.getBoundingClientRect();

    this.copyBtnPosition = {
      top: targetRect.bottom - containerRect.top - 23,
      left: Math.min(targetRect.right - containerRect.left + 35, containerRect.width - 40)
    };
  }

  /**
   * Appends the currently selected text (including any equations) to the chat
   * input field via the InputService, then resets all selection state and
   * removes the active browser selection.
   */
  public copySelectionToInput(): void {
    if (this.selectedText) {
      this._inputService.queueAppendToInput(this.selectedText);
    }
    this.showCopyToInputIcon = false;
    this.selectedText = '';
    window.getSelection()?.removeAllRanges();
  }

  /**
   * Listens for any pointerdown event on the document and dismisses the copy
   * button unless the click target is the button itself, allowing the user to
   * click elsewhere to cancel the selection flow.
   */
  @HostListener('document:pointerdown', ['$event'])
  public onDocumentPointerDown(event: PointerEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.copy-to-input-btn')) return;
    if (this._elementRef.nativeElement.contains(target)) return;
    if (this.showCopyToInputIcon) {
      this._suppressNextPointerUp = true;
    }
    this.showCopyToInputIcon = false;
    this.selectedText = '';
    this._cdr.markForCheck();
  }


  /**
   * Resets the copy button state and hides it. Used when the user successfully copies text to input or when the selection becomes invalid.
   */
  private clearCopyState(): void {
    this.showCopyToInputIcon = false;
    this.selectedText = '';
  }

  /**
   * Checks the current selection and returns a Range if there is a valid selection that originates within the host element. Returns null if there is no selection,
   * the selection is collapsed, or the selection does not originate within the host.
   * @returns A Range object representing the current selection within the host, or null if no valid selection exists.
   */
  private getActiveRangeWithinHost(): Range | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return null;
    }
    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      return null;
    }

    const host = this._elementRef.nativeElement;
    const container = range.commonAncestorContainer;

    if (!host.contains(container)) {
      return null;
    }

    return range;
  }

  /**
   * Attempts to select an equation element from the last pointer down target.
   * This is useful for handling non-text nodes that can lead to collapsed selections.
   * @returns True if an equation was successfully selected, false otherwise.
   */
  private trySelectEquationFromPointerTarget(): boolean {
    const target = this._lastPointerDownTarget as Node | null;
    if (!target) {
      return false;
    }
    const element = target instanceof Element ? target : target.parentElement;
    const container = element?.closest('mjx-container') ?? null;
    if (!container) {
      return false;
    }

    if (!this._elementRef.nativeElement.contains(container)) {
      return false;
    }

    const selection = window.getSelection();
    if (!selection) {
      return false;
    }

    const range = document.createRange();
    range.selectNode(container);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  }

  /**
   * Extracts the selected text, including any equations, from the given range.
   * @param range The range object representing the current selection.
   * @returns A string containing the extracted selection with equations.
   */
  private getExtractedSelection(range: Range): string {
    return this._equationService
      .extractSelectionWithEquations(range, this._elementRef)
      .trim();
  }

}